import React, { useState, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';

const Label = ({ children, tooltip }) => (
    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {children}
        {tooltip && <span title={tooltip} style={{ cursor: 'help', color: '#9ca3af', fontSize: 13 }}>ℹ</span>}
    </label>
);
const Hint  = ({ children }) => <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>{children}</p>;
const Card  = ({ title, icon, children }) => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 24, marginBottom: 20 }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{icon}</span> {title}
        </p>
        {children}
    </div>
);

function TagInput({ value = [], onChange, placeholder, color = '#1565c0', bg = '#eff6ff' }) {
    const [input, setInput] = useState('');
    const add = () => {
        const v = input.trim().toLowerCase();
        if (!v || value.includes(v)) return;
        onChange([...value, v]);
        setInput('');
    };
    return (
        <div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {value.map((v, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: bg, borderRadius: 20, fontSize: 12, color, fontWeight: 600, border: `1px solid ${color}30` }}>
                        {v}
                        <button onClick={() => onChange(value.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color, padding: 0, lineHeight: 1, fontSize: 14 }}>×</button>
                    </span>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <input value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && add()}
                    placeholder={placeholder}
                    style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }}/>
                <button onClick={add}
                    style={{ padding: '8px 16px', background: color, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    Add
                </button>
            </div>
        </div>
    );
}

function Toggle({ value, onChange, label }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{label}</span>
            <div onClick={() => onChange(!value)}
                style={{ width: 44, height: 24, borderRadius: 12, background: value ? '#1565c0' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                <div style={{ position: 'absolute', top: 2, left: value ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}/>
            </div>
        </div>
    );
}

export default function PlatformSettings() {
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [form,    setForm]    = useState({
        inbound_block_keywords:  [],
        profanity_list:          [],
        outbound_block_keywords: [],
        frustration_threshold:   3,
        max_response_length:     2000,
        ai_disclosure_enabled:   true,
        sensitive_data_enabled:  true,
        block_response:          'I am unable to process this request.',
    });

    const token = localStorage.getItem('token');
    const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        (async () => {
            try {
                const res  = await fetch(`${url}/guardrails/platform`, { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                if (data.success) setForm(p => ({ ...p, ...data.data }));
            } catch (e) { toast.error('Failed to load settings'); }
            setLoading(false);
        })();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                inbound_block_keywords:  form.inbound_block_keywords,
                profanity_list:          form.profanity_list,
                outbound_block_keywords: form.outbound_block_keywords,
                frustration_threshold:   form.frustration_threshold,
                max_response_length:     form.max_response_length,
                ai_disclosure_enabled:   form.ai_disclosure_enabled,
                ai_disclosure_response:  form.ai_disclosure_response,
                sensitive_data_enabled:  form.sensitive_data_enabled,
                block_response:          form.block_response,
            };
            const res  = await fetch(`${url}/guardrails/platform`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (data.success) toast.success('Platform guardrails saved ✅');
            else toast.error(data.error);
        } catch (e) { toast.error('Save failed'); }
        setSaving(false);
    };

    if (loading) return (
        <WrapperComponent>
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
                <div className="spinner-border text-primary"></div>
            </div>
        </WrapperComponent>
    );

    return (
        <WrapperComponent>
            <div style={{ maxWidth: 800, margin: '0 auto', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
                    <div>
                        <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Platform Settings</h5>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                            Rules that apply to ALL agents across ALL tenants. Set by super admin only.
                        </p>
                    </div>
                    <button onClick={save} disabled={saving}
                        style={{ padding: '10px 24px', background: 'linear-gradient(135deg, #1565c0, #1e40af)', color: '#fff', border: 'none', borderRadius: 12, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1 }}>
                        {saving ? <><div className="spinner-border spinner-border-sm text-light" style={{ width: '1rem', height: '1rem' }}></div> Saving...</> : <><i className="bi bi-check-lg"></i> Save Changes</>}
                    </button>
                </div>

                {/* AI Safety */}
                <Card title="AI Safety" icon="🤖">
                    <Toggle value={form.ai_disclosure_enabled} onChange={v => set('ai_disclosure_enabled', v)}
                        label="Always disclose AI identity when customer asks"/>
                    <Toggle value={form.sensitive_data_enabled} onChange={v => set('sensitive_data_enabled', v)}
                        label="Auto-remove sensitive data (card numbers, OTP) from responses"/>
                    <div style={{ marginTop: 16 }}>
                        <Label tooltip="Message shown when customer asks if they are talking to a bot">AI Disclosure Response</Label>
                        <textarea value={form.ai_disclosure_response || ''}
                            onChange={e => set('ai_disclosure_response', e.target.value)}
                            rows={2} placeholder="Yes, I am an AI assistant. Type 'agent' to speak with a human."
                            style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
                    </div>
                </Card>

                {/* Profanity Filter */}
                <Card title="Profanity Filter" icon="🚫">
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
                        Platform already blocks common English and Hindi profanity. Add additional words specific to your context.
                    </p>
                    <Label tooltip="Additional profanity words to block (in any language). Platform defaults already included.">
                        Additional Profanity Words
                    </Label>
                    <TagInput value={form.profanity_list} onChange={v => set('profanity_list', v)}
                        placeholder="Add word and press Enter" color="#dc2626" bg="#fef2f2"/>
                    <Hint>Common English and Hindi profanity is already blocked by default. Only add domain-specific or regional words here.</Hint>
                </Card>

                {/* Inbound Guardrails */}
                <Card title="Inbound Controls" icon="📥">
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
                        Control what messages from users are allowed to be processed.
                    </p>
                    <div style={{ marginBottom: 20 }}>
                        <Label tooltip="If user message contains any of these words — block it and show block response">
                            Block Keywords (Inbound)
                        </Label>
                        <TagInput value={form.inbound_block_keywords} onChange={v => set('inbound_block_keywords', v)}
                            placeholder="e.g. hack, exploit, jailbreak" color="#7c3aed" bg="#faf5ff"/>
                        <Hint>Messages containing these words will be blocked before agent processes them.</Hint>
                    </div>
                    <div style={{ marginBottom: 20 }}>
                        <Label tooltip="Number of frustrated messages before auto-escalating to human agent">
                            Frustration Threshold
                        </Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <input type="range" min={1} max={10} value={form.frustration_threshold}
                                onChange={e => set('frustration_threshold', parseInt(e.target.value))}
                                style={{ flex: 1 }}/>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1565c0', minWidth: 30 }}>{form.frustration_threshold}</span>
                        </div>
                        <Hint>Auto-escalate to human agent after this many frustrated messages from user.</Hint>
                    </div>
                    <div>
                        <Label>Block Response Message</Label>
                        <input value={form.block_response} onChange={e => set('block_response', e.target.value)}
                            style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                            placeholder="I am unable to process this request."/>
                        <Hint>Message shown when a user message is blocked.</Hint>
                    </div>
                </Card>

                {/* Outbound Guardrails */}
                <Card title="Outbound Controls" icon="📤">
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
                        Control what agents are allowed to say in their responses.
                    </p>
                    <div style={{ marginBottom: 20 }}>
                        <Label tooltip="If agent response contains any of these words — replace entire response with fallback">
                            Block Keywords (Outbound)
                        </Label>
                        <TagInput value={form.outbound_block_keywords} onChange={v => set('outbound_block_keywords', v)}
                            placeholder="e.g. guaranteed, promise, definitely" color="#dc2626" bg="#fef2f2"/>
                        <Hint>Agent responses containing these words will be replaced with fallback message.</Hint>
                    </div>
                    <div>
                        <Label tooltip="Maximum character length of any agent response. Longer responses will be truncated.">
                            Maximum Response Length (characters)
                        </Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <input type="range" min={50} max={4000} step={100}
                                value={form.max_response_length}
                                onChange={e => set('max_response_length', parseInt(e.target.value))}
                                style={{ flex: 1 }}/>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1565c0', minWidth: 50 }}>{form.max_response_length}</span>
                        </div>
                        <Hint>Responses longer than this will be truncated. Recommended: 2000 characters for WhatsApp.</Hint>
                    </div>
                </Card>

            </div>
            <ToastContainer/>
        </WrapperComponent>
    );
}
